/**
 * Settings: this browser's appearance, the mail account, and the instance-wide tunables that used to
 * be compile-time constants. Backups are the host's (pages/host-backups.ts).
 *
 * The port of `Views/SettingsPage.xaml` + `SettingsViewModel`; the two hand-written cards are
 * `sections/mail-account.ts` and `sections/paypal.ts`, and the forty-odd numbers are the table in
 * `settingRows.ts`.
 *
 *   * ANY STAFF USER MAY LOOK; ONLY AN ADMIN MAY CHANGE. The page is read-only for a technician rather
 *     than hidden - "that transfer was too large" is only actionable next to the number it means.
 *   * APPEARANCE IS NOT GATED. Everything else is an instance-wide limit; this is one person's choice
 *     in one browser, applied at once with nothing to save. It is the same stored choice the rail's
 *     buttons show, and both follow each other (and another tab) through `onChoiceChanged`.
 *   * THE SERVER IS THE AUTHORITY on what is acceptable: it clamps and answers with what it stored, and
 *     the page re-binds to that, so a narrowed value visibly snaps to the number in force. The answer
 *     is also adopted into this browser's own copy (`api/settings.ts`), which several pages quote.
 *   * TABS AND FOLDING CARDS keep it to a screen or two: the tunables are grouped onto the tabs their
 *     subject belongs to, each section folds, and every tab with tunables carries its own Save. They
 *     are still ONE document saved by one request, so every panel is built up front and kept, and a
 *     Save on any tab saves every tab. `?tab=` deep-links a tab, and the last one is remembered.
 */
import type { Button, CheckBox, Container, StackPanel, TextBox } from 'webwin';
import { isAdmin } from '../api/auth.js';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import { adopt } from '../api/settings.js';
import type { AppSettingsDto, AppSettingsResponse } from '../contracts.gen.js';
import { localDateTime } from '../format.js';
import {
  buildSettings,
  defaultHint,
  displayText,
  filled,
  rowKey,
  SETTING_SECTIONS,
  storedValue,
  toStored,
  type SettingRowSpec,
  type SettingsTabKey
} from '../settingRows.js';
import * as storage from '../storage.js';
import { currentChoice, onChoiceChanged, setChoice, type ThemeChoice } from '../theme/store.js';
import { collapsible, type Collapsible } from '../ui/Collapsible.js';
import { confirm } from '../ui/dialogs.js';
import { StatusLine } from '../ui/StatusLine.js';
import { TabStrip } from '../ui/TabStrip.js';
import {
  button,
  buttonRow,
  card,
  checkBox,
  comboBox,
  FONT,
  GAP,
  h1,
  h2,
  muted,
  relayout,
  scrollPage,
  stackOf,
  text,
  textBox,
  value,
  vstack,
  type CrmLabel
} from '../ui/layout.js';
import { mailAccountSection } from './sections/mail-account.js';
import { payPalSection } from './sections/paypal.js';
import { androidAccountSection } from './sections/android-account.js';
import { ticketAlertSection } from './sections/ticket-alerts.js';
import type { CrmPage, PageContext } from './page.js';

const THEMES: ReadonlyArray<readonly [ThemeChoice, string]> = [
  ['light', 'Light'],
  ['dark', 'Dark'],
  ['auto', 'Match system']
];

interface RowControls {
  spec: SettingRowSpec;
  box: TextBox;
  unlimited: CheckBox | null;
  hint: CrmLabel;
  tab: SettingsTabKey;
  region: Collapsible;
}

const TABS: ReadonlyArray<{ key: SettingsTabKey; label: string }> = [
  { key: 'general', label: 'General' },
  { key: 'security', label: 'Security' },
  { key: 'mail', label: 'Mail' },
  { key: 'payments', label: 'Payments' },
  { key: 'devices', label: 'Devices & agents' },
  { key: 'mobile', label: 'Mobile devices' },
  { key: 'files', label: 'Files & transfers' }
];
const TAB_STORAGE_KEY = 'jetsoncrm.app.settingsTab';

function initialTab(query: URLSearchParams, tabs: typeof TABS): SettingsTabKey {
  const wanted = query.get('tab') ?? storage.read(TAB_STORAGE_KEY);
  return tabs.find(t => t.key === wanted)?.key ?? 'general';
}

export function settingsPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      const canEdit = isAdmin();
      const { view, content } = scrollPage();
      host.controls.add(view);

      const statuses: StatusLine[] = [];
      const lastChangedLabels: CrmLabel[] = [];
      const saveButtons: Button[] = [];
      const otherButtons: Button[] = [];
      let defaults: AppSettingsDto | null = null;
      let loaded: AppSettingsDto | null = null;

      const say = (kind: 'ok' | 'error' | 'busy' | 'clear', message = ''): void => {
        for (const s of statuses) {
          if (kind === 'clear') s.clear();
          else s[kind](message);
        }
      };

      content.controls.add(
        stackOf(
          vstack({ spacing: GAP.sm }),
          h1('Settings'),
          muted(
            "This browser's appearance, the mail and PayPal accounts, and the limits and intervals that apply to this whole CRM instance."
          )
        )
      );

      if (!canEdit) {
        const notice = card({ tint: 'info' });
        notice.controls.add(
          text('Everything here but Appearance applies to the whole instance, so only an administrator can change it.')
        );
        content.controls.add(notice);
      }

      // =================================================================
      //  Tabs. Every panel is built up front and kept: the tunables are one document saved by one
      //  request, so a value edited on one tab must still be there when Save is pressed on another.
      // =================================================================

      const visibleTabs = TABS;
      const panels = new Map<SettingsTabKey, StackPanel>();
      for (const t of visibleTabs) panels.set(t.key, vstack({ spacing: GAP.lg }));
      const panel = (key: SettingsTabKey): StackPanel => panels.get(key)!;

      const tabHost = vstack({ spacing: 0, hAlign: 'stretch', autoWidth: false });
      const tabs = new TabStrip(
        tabHost,
        visibleTabs.map(t => ({ key: t.key, label: t.label, panel: panel(t.key) }))
      );
      content.controls.add(tabs.view);
      content.controls.add(tabHost);

      // =================================================================
      //  General: appearance, never read-only
      // =================================================================

      const themePicker = comboBox<string>(
        THEMES.map(t => t[1]),
        { selectedIndex: Math.max(0, THEMES.findIndex(t => t[0] === currentChoice())), width: 220, hAlign: 'start' }
      );
      themePicker.on('selectionchanged', () => {
        const choice = THEMES[themePicker.selectedIndex]?.[0];
        if (choice && choice !== currentChoice()) setChoice(choice);
      });
      ctx.dispose.add(
        onChoiceChanged(choice => {
          const index = THEMES.findIndex(t => t[0] === choice);
          if (index >= 0 && themePicker.selectedIndex !== index) themePicker.selectedIndex = index;
        })
      );
      const appearance = card();
      appearance.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Appearance'),
          muted(
            'How JetsonCRM looks in this browser - the CRM and the knowledge base share this setting. It is remembered in this browser only, not on your account.'
          ),
          themePicker
        )
      );
      panel('general').controls.add(appearance);

      // =================================================================
      //  Security: pointer to the Security page (admin - the page refuses anyone else)
      // =================================================================

      if (canEdit) {
        const pointer = card();
        pointer.controls.add(
          stackOf(
            vstack({ spacing: GAP.md }),
            h2('Locked-out accounts and the sign-in log'),
            muted(
              'Locked-out accounts, the log of every refused sign-in, and who is emailed when an account is locked out all live on the Security page. The numbers that govern them are below.'
            ),
            stackOf(buttonRow(), button('Open Security', () => ctx.go('/security')))
          )
        );
        panel('security').controls.add(pointer);
      }

      // =================================================================
      //  The cards with their own Save
      // =================================================================

      const mail = mailAccountSection(ctx, canEdit);
      const payPal = payPalSection(ctx, canEdit);
      const androidAccount = androidAccountSection(ctx, canEdit);
      const ticketAlerts = ticketAlertSection(ctx, canEdit);
      panel('mail').controls.addRange([mail.card, ticketAlerts.card]);
      panel('payments').controls.add(payPal.card);
      panel('mobile').controls.add(androidAccount.card);
      // The server's own builds and every organization's backups are the host's: see
      // pages/server-updates.ts and pages/host-backups.ts.

      // =================================================================
      //  The tunables: one folding card per section, under a Save bar on every tab that has any.
      //  All of them save together - they are one document - so a bar on any tab saves every tab.
      // =================================================================

      const controls: RowControls[] = [];
      const tunableTabs = TABS.filter(t => SETTING_SECTIONS.some(s => s.tab === t.key));

      for (const t of tunableTabs) {
        const top = new StatusLine();
        const bottom = new StatusLine();
        const lastChanged = muted('');
        statuses.push(top, bottom);
        lastChangedLabels.push(lastChanged);

        const reloadButton = button('Reload', () => void load());
        otherButtons.push(reloadButton);
        const bar = buttonRow();
        if (canEdit) {
          const saveTop = button('Save limits', () => void save(), true);
          const resetButton = button('Restore defaults', () => void reset());
          saveButtons.push(saveTop);
          otherButtons.push(resetButton);
          bar.controls.addRange([saveTop, reloadButton, resetButton]);
        } else {
          bar.controls.add(reloadButton);
        }

        const sectionsOnTab = SETTING_SECTIONS.filter(s => s.tab === t.key);
        const regions = vstack({ spacing: GAP.md });
        for (const section of sectionsOnTab) {
          // A tab holding a single section opens it: folding the only thing on the tab saves no
          // scrolling and costs a click.
          const region = collapsible(section.title, { expanded: sectionsOnTab.length === 1 });
          region.setSummary(`${section.rows.length} ${section.rows.length === 1 ? 'setting' : 'settings'}`);
          region.body.controls.add(muted(section.description));
          for (const spec of section.rows) {
            const box = textBox({ width: 120, hAlign: 'start', enabled: canEdit });
            const unlimited = spec.allowUnlimited ? checkBox('Unlimited', false, { enabled: canEdit }) : null;
            // Disabled rather than cleared, so the number the operator would go back to stays visible.
            unlimited?.on('change', () => {
              box.enabled = canEdit && !unlimited.checked;
            });
            const hint = muted('');
            const inputs = stackOf(buttonRow({ spacing: GAP.md, align: 'center' }), box, value(spec.unit));
            if (unlimited) inputs.controls.add(unlimited);
            const row = stackOf(vstack({ spacing: GAP.xs }), text(spec.label, { font: FONT.bold }));
            if (spec.help) row.controls.add(muted(spec.help));
            row.controls.add(inputs);
            row.controls.add(hint);
            region.body.controls.add(row);
            controls.push({ spec, box, unlimited, hint, tab: t.key, region });
          }
          regions.controls.add(region.card);
        }

        const footer = stackOf(
          vstack({ spacing: GAP.sm }),
          muted(
            'Values outside a workable range are narrowed by the server when they are saved, and the page then shows what was actually stored.'
          )
        );
        if (canEdit) {
          const saveBottom = button('Save limits', () => void save(), true);
          saveButtons.push(saveBottom);
          footer.controls.add(stackOf(buttonRow(), saveBottom));
        }
        footer.controls.add(bottom.label);

        panel(t.key).controls.addRange([
          stackOf(
            vstack({ spacing: GAP.sm }),
            h2(t.key === 'mail' ? 'Mail limits' : 'Limits'),
            muted(
              'One set of limits across every tab: saving here saves them all and pushes them to each connected agent; devices that are offline pick them up when they reconnect.'
            ),
            bar,
            top.label,
            lastChanged
          ),
          regions,
          footer
        ]);
      }

      tabs.select(initialTab(ctx.query, visibleTabs));
      tabs.onChange(key => {
        storage.write(TAB_STORAGE_KEY, key);
        const url = new URL(window.location.href);
        url.searchParams.set('tab', key);
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        relayout(panel(key as SettingsTabKey));
      });

      function bind(response: AppSettingsResponse): void {
        adopt(response.settings);
        defaults = response.defaults;
        loaded = filled(response.settings, response.defaults);
        for (const c of controls) {
          const fallback = storedValue(defaults, c.spec) ?? 0;
          const stored = storedValue(loaded, c.spec) ?? fallback;
          c.box.text = displayText(c.spec, stored, fallback);
          if (c.unlimited) c.unlimited.checked = stored <= 0;
          c.box.enabled = canEdit && !(c.unlimited?.checked ?? false);
          c.hint.text = defaultHint(c.spec, fallback);
        }
        const changed = response.updatedUtc
          ? `Last changed ${localDateTime(response.updatedUtc)}${response.updatedBy?.trim() ? ` by ${response.updatedBy}` : ''}.`
          : 'Never changed - running on the shipped defaults.';
        for (const label of lastChangedLabels) label.text = changed;
        relayout(content);
      }

      function setBusy(busy: boolean): void {
        for (const b of [...saveButtons, ...otherButtons]) b.enabled = !busy;
      }

      async function load(): Promise<void> {
        setBusy(true);
        say('busy', 'Loading…');
        try {
          const response = await crm.getSettings(ctx.dispose.signal());
          if (ctx.cancelled()) return;
          bind(response);
          say('clear');
        } catch (error) {
          if (!ctx.cancelled()) say('error', 'Could not load the settings: ' + describeError(error));
        } finally {
          setBusy(false);
        }
      }

      async function save(): Promise<void> {
        if (!loaded) return;
        const values = new Map<string, number>();
        for (const c of controls) {
          const stored = toStored(c.spec, c.box.text, c.unlimited?.checked ?? false);
          if (stored === null) {
            // The row may be on another tab, folded away: show it, or the message names a field
            // nobody can see.
            tabs.select(c.tab);
            c.region.setExpanded(true);
            say('error', `“${c.spec.label}” needs a whole number of ${c.spec.unit}${c.spec.allowUnlimited ? ' (or tick Unlimited)' : ''}.`);
            return;
          }
          values.set(rowKey(c.spec), stored);
        }
        setBusy(true);
        say('busy', 'Saving…');
        try {
          const response = await crm.saveSettings(buildSettings(loaded, values));
          if (ctx.cancelled()) return;
          // Re-bound to what the server stored, not what was sent: it clamps, and a page still showing
          // the rejected number would be describing a limit nothing is enforcing.
          bind(response);
          say('ok', 'Saved. Connected agents were updated; offline ones apply this when they reconnect.');
        } catch (error) {
          if (!ctx.cancelled()) say('error', 'Could not save the settings: ' + describeError(error));
        } finally {
          setBusy(false);
        }
      }

      async function reset(): Promise<void> {
        const yes = await confirm(ctx.desktop, {
          title: 'Restore defaults',
          message:
            'Put every limit on every tab back to the value JetsonCRM shipped with?\n\nThis applies to the whole instance and is pushed to every connected agent. The mail account is not affected.'
        });
        if (!yes || ctx.cancelled()) return;
        setBusy(true);
        say('busy', 'Restoring defaults…');
        try {
          const response = await crm.resetSettings();
          if (ctx.cancelled()) return;
          bind(response);
          say('ok', 'Restored the shipped defaults.');
        } catch (error) {
          if (!ctx.cancelled()) say('error', 'Could not restore the default settings: ' + describeError(error));
        } finally {
          setBusy(false);
        }
      }

      void (async () => {
        await Promise.all([load(), mail.load(), payPal.load(), androidAccount.load(), ticketAlerts.load()]);
        if (!ctx.cancelled()) relayout(content);
      })();
    }
  };
}
